import React from "react";
import { Loader2, Inbox } from "lucide-react";

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Spinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-7 w-7 animate-spin text-primary" />
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, text, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card/50 py-16 text-center">
      <Icon className="mb-3 h-10 w-10 text-muted-foreground" />
      <p className="font-display text-xl font-semibold">{title}</p>
      {text && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{text}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function PrimaryButton({ children, className = "", ...props }) {
  return (
    <button {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:bg-[#FF8533] disabled:opacity-60 ${className}`}>
      {children}
    </button>
  );
}

export function GhostButton({ children, className = "", ...props }) {
  return (
    <button {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-secondary px-4 py-2.5 text-sm font-semibold text-foreground transition-all hover:border-primary/50 disabled:opacity-60 ${className}`}>
      {children}
    </button>
  );
}

export function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-border bg-card p-5 sm:p-6 ${className}`}>{children}</div>;
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative my-4 w-full ${wide ? "max-w-3xl" : "max-w-lg"} rounded-2xl border border-border bg-card p-6 shadow-2xl animate-fadeUp`}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-2xl font-bold">{title}</h2>
          <button onClick={onClose} data-testid="modal-close" className="rounded-lg p-1 text-muted-foreground hover:bg-accent hover:text-foreground">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Input({ label, testId, className = "", ...props }) {
  return (
    <div className={className}>
      {label && <label className="mb-1.5 block text-sm font-medium">{label}</label>}
      <input data-testid={testId} {...props}
        className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
    </div>
  );
}

export function Textarea({ label, testId, className = "", ...props }) {
  return (
    <div className={className}>
      {label && <label className="mb-1.5 block text-sm font-medium">{label}</label>}
      <textarea data-testid={testId} {...props}
        className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
    </div>
  );
}

export function Select({ label, testId, children, className = "", ...props }) {
  return (
    <div className={className}>
      {label && <label className="mb-1.5 block text-sm font-medium">{label}</label>}
      <select data-testid={testId} {...props}
        className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary">
        {children}
      </select>
    </div>
  );
}

export function Table({ head, children }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-background/40 text-left font-mono text-xs uppercase tracking-wide text-muted-foreground">
            {head.map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
